<script setup lang="ts">
    import type { StoryblokRichtext } from '~/.storyblok/types/storyblok';
    import type { FormPresentationNode, FrontendFieldDefinition, RichTextDocument } from '@hmmh/storyblok-forms';
    import type { FieldComponentResolver } from './fieldComponentResolver';

    const props = defineProps<{
        nodes: FormPresentationNode[];
        fields: FrontendFieldDefinition[];
        fieldComponentResolver: FieldComponentResolver;
        labelPosition?: 'inside' | 'outside' | '';
    }>();

    defineOptions({ name: 'FormPresentation' });

    const fieldsByName = computed(() => new Map(props.fields.map((field) => [field.name, field])));
    const asStoryblokRichtext = (document: RichTextDocument): StoryblokRichtext =>
        document as unknown as StoryblokRichtext;
</script>

<template>
    <template
        v-for="(node, index) in nodes"
        :key="node.kind === 'field' ? node.name : `${node.kind}-${index}`"
    >
        <template v-if="node.kind === 'field'">
            <component
                :is="fieldComponentResolver.resolve(fieldsByName.get(node.name)!.type)"
                v-if="fieldsByName.get(node.name) && fieldComponentResolver.resolve(fieldsByName.get(node.name)!.type)"
                v-editable="node.blok"
                :field="fieldsByName.get(node.name)"
            />
            <p v-else-if="fieldsByName.get(node.name)">Unknown field type: {{ fieldsByName.get(node.name)!.type }}</p>
        </template>

        <fieldset
            v-else-if="node.kind === 'fieldset'"
            v-editable="node.blok"
            class="form-presentation__fieldset"
        >
            <legend
                v-if="node.legend"
                :class="{ '--labels-inside': props.labelPosition === 'inside' }"
            >
                {{ node.legend }}
            </legend>
            <FormPresentation
                :nodes="node.children"
                :fields="fields"
                :field-component-resolver="fieldComponentResolver"
                :label-position="labelPosition"
            />
        </fieldset>

        <div
            v-else-if="node.kind === 'static-text'"
            v-editable="node.blok"
            class="text form-presentation__static-text"
        >
            <RichText :doc="asStoryblokRichtext(node.bodytext)" />
        </div>

        <div
            v-else
            v-editable="node.blok"
            class="form-presentation__row"
            :class="node.layout ? `form-presentation__row--${node.layout}` : undefined"
        >
            <FormPresentation
                :nodes="node.children"
                :fields="fields"
                :field-component-resolver="fieldComponentResolver"
            />
        </div>
    </template>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/text/text.scss';

    .form-presentation__fieldset {
        border: 0;
        display: flex;
        flex-direction: column;
        gap: $spacing;
        margin: 0;
        min-width: 0;
        padding: 0;

        legend.--labels-inside {
            margin-bottom: var(--spacing);
        }
    }

    .form-presentation__row {
        display: grid;
        gap: $spacing;
        grid-template-columns: minmax(0, 1fr);

        @media (min-width: $br-m) {
            &--1 {
                grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            }

            &--2 {
                grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
            }

            &--3 {
                grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
            }
        }
    }
</style>
